import { useEffect, useState } from 'react'
import Client from '../../../../../client'
import { DepopSizeSet } from '../types'
import { getErrorString } from '../../../../../utils/serverErrorUtils'

interface DepopSizes {
  sizeSet: DepopSizeSet
  // The category `sizeSet` was fetched for. An empty sizeSet alone cannot tell "category has no
  // sizes" apart from "not fetched yet", and acting on that difference wipes saved sizes.
  loadedFor?: number
  loading: boolean
  error: string
}

const NO_SIZES: DepopSizeSet = { sizeSetId: null, displayName: null, sizes: [] }

// Which sizes Depop offers depends on the selected category, so this refetches whenever
// `categoryMarketCategoryId` changes — the same shape as useDepopCategorySpecifics. Categories without
// sizes (accessories, jewellery, home goods) return an empty `sizes` list rather than an error, so
// callers can render nothing.
export const useDepopSizes = (categoryMarketCategoryId?: number): DepopSizes => {
  const [sizeSet, setSizeSet] = useState<DepopSizeSet>(NO_SIZES)
  const [loadedFor, setLoadedFor] = useState<number | undefined>(undefined)
  // Starts true when there is a category to fetch for: the effect below cannot flip it before the
  // first render commits, and one render claiming "loaded, no sizes" is enough to wipe a saved size.
  const [loading, setLoading] = useState(!!categoryMarketCategoryId)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!categoryMarketCategoryId) {
      setSizeSet(NO_SIZES)
      setLoadedFor(undefined)
      setLoading(false)
      setError('')
      return undefined
    }

    let cancelled = false
    setLoading(true)
    setError('')

    Client.depopCategorySizes(categoryMarketCategoryId)
      .then(res => {
        if (cancelled) return
        setSizeSet(res.data || NO_SIZES)
        setLoadedFor(categoryMarketCategoryId)
        setError('')
      })
      .catch(e => {
        if (cancelled) return
        setSizeSet(NO_SIZES)
        setLoadedFor(undefined)
        setError(getErrorString(e) || 'Failed to load Depop sizes')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [categoryMarketCategoryId])

  return { sizeSet, loadedFor, loading, error }
}
